import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { focusFormError } from '../../../functions/focus-form-error';
import { ListItem } from '../../../models/api/core/list/list-item';
import { SearchInput } from '../../../models/api/view/search/search-input';
import { ValidatorsEx } from '../../../validators/validators-ex';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import {
  ClaimNumberComponent,
  SearchType,
} from '../../core/controls/claim-number.component';
import { DatePickerComponent } from '../../core/controls/date-picker.component';
import { InputComponent } from '../../core/controls/input.component';
import { RangeComponent } from '../../core/controls/range.component';
import { SelectComponent } from '../../core/controls/select.component';
import { SsnComponent, SsnType } from '../../core/controls/ssn.component';
import { ToggleSwitchComponent } from '../../core/controls/toggle-switch.component';
import { ToastComponent } from '../../core/labels/toast.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { QuickSearchOption } from '../../layout/quick-search.component';

@Component({
  selector: 'app-search-form',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    ButtonComponent,
    ActionRowComponent,
    ToastComponent,
    DatePickerComponent,
    RangeComponent,
    SsnComponent,
    ToggleSwitchComponent,
    ClaimNumberComponent,
  ],
  template: `
    <form (submit)="formSubmit()" [formGroup]="fg">
      <h2
        class="tw-text-base tw-w-full tw-font-bold tw-pl-1.5 tw-py-2 tw-text-[var(--sed-engaged-black-color)]"
      >
        Claimant Info
      </h2>

      <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-0">
        <app-claim-number
          #searchType
          class="tw-w-full md:tw-w-1/4 tw-min-w-0"
          [control]="fg.controls.claimNumber"
          [maxlength]="50"
        />
        <app-input
          class="tw-w-full md:tw-w-1/4 tw-min-w-0"
          [control]="fg.controls.firstName"
          label="First name"
          [maxlength]="50"
          type="Text"
        />
        <app-input
          class="tw-w-full md:tw-w-1/4 tw-min-w-0"
          [control]="fg.controls.lastName"
          label="Last name"
          [maxlength]="50"
          type="Text"
        />
        <app-input
          class="tw-w-full md:tw-w-1/4 tw-min-w-0"
          [control]="fg.controls.employeeId"
          label="Employee ID"
          [maxlength]="30"
          type="Text"
        />
      </div>
      <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-0">
        @if (showSsn) {
          <app-ssn
            class="tw-w-full md:tw-w-1/4 tw-min-w-0"
            [control]="fg.controls.ssn"
            label="SSN"
            [type]="ssnType"
          />
        }
        <app-date-picker
          class="tw-w-full md:tw-w-1/4 tw-min-w-0"
          [control]="fg.controls.birthDate"
          label="Birth date"
        />
        <app-input
          class="tw-w-full md:tw-w-1/4 tw-min-w-0"
          [control]="fg.controls.phoneNumber"
          label="Phone number"
          type="Phone"
        />
        <app-select
          [options]="searchOptions"
          optionLabel="description"
          optionValue="value"
          class="tw-w-full md:tw-w-1/4 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.searchOptions"
          label="Search options"
          [multiple]="true"
        />
      </div>

      <h2
        class="tw-text-base tw-w-full tw-font-bold tw-pl-1.5 tw-py-2 tw-text-[var(--sed-engaged-black-color)]"
      >
        Claim Info
      </h2>

      <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-0">
        <app-select
          [options]="lob"
          optionLabel="description"
          optionValue="value"
          [control]="fg.controls.lob"
          label="Line of business"
          class="tw-w-full md:tw-w-1/5 tw-min-w-0"
          [multiple]="true"
        />
        <app-select
          [options]="claimStatus"
          optionLabel="description"
          optionValue="value"
          [control]="fg.controls.claimStatus"
          label="Status"
          class="tw-w-full md:tw-w-1/5 tw-min-w-0 chkclaimstatus"
          [multiple]="true"
        />
        <app-select
          [options]="claimSubstatus"
          optionLabel="description"
          optionValue="value"
          [control]="fg.controls.claimSubstatus"
          label="Substatus"
          class="tw-w-full md:tw-w-1/5 tw-min-w-0"
          [multiple]="true"
        />
        <app-select
          [options]="claimType"
          optionLabel="description"
          optionValue="value"
          [control]="fg.controls.claimType"
          label="Claim type"
          class="tw-w-full md:tw-w-1/5 tw-min-w-0"
          [group]="true"
          [multiple]="true"
        />

        <app-select
          [options]="caseType"
          optionLabel="description"
          optionValue="value"
          [control]="fg.controls.caseType"
          label="Case type"
          class="tw-w-full md:tw-w-1/5 tw-min-w-0"
          [multiple]="true"
        />
      </div>

      <div
        class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 tw-pt-2 md:tw-gap-0"
      >
        <app-select
          [options]="claimFlag"
          optionLabel="description"
          optionValue="value"
          [control]="fg.controls.claimFlags"
          label="Claim flags"
          class="tw-w-full md:tw-basis-1/4 tw-min-w-0"
          [multiple]="true"
        />
        <app-input
          class="tw-w-full md:tw-basis-1/4 tw-min-w-0"
          [control]="fg.controls.policyNumber"
          label="Policy number"
          [maxlength]="40"
          type="Text"
        />
        <app-input
          class="tw-w-full md:tw-basis-1/4 tw-min-w-0"
          [control]="fg.controls.examinerName"
          label="Examiner ID"
          [maxlength]="40"
          type="Text"
        />
        <app-input
          class="tw-w-full md:tw-basis-1/4 tw-min-w-0"
          [control]="fg.controls.driverName"
          label="Driver name"
          [maxlength]="60"
          type="Text"
        />
      </div>

      <h2
        class="tw-text-base tw-w-full tw-font-bold tw-pl-1.5 tw-py-2 tw-text-[var(--sed-engaged-black-color)]"
      >
        Location
      </h2>
      <div class="tw-pb-0">
        <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-0">
          <app-select
            [options]="client"
            optionLabel="description"
            optionValue="value"
            [control]="fg.controls.client"
            label="Client"
            class="tw-w-full md:tw-w-1/3 tw-min-w-0"
          />
          <app-select
            [options]="account"
            optionLabel="description"
            optionValue="value"
            [control]="fg.controls.account"
            label="Account"
            class="tw-w-full md:tw-w-1/3 tw-min-w-0"
          />
          <app-select
            [options]="unit"
            optionLabel="description"
            optionValue="value"
            [control]="fg.controls.unit"
            label="Unit"
            [placeholder]="unitLoading ? 'Units loading' : ''"
            [loading]="unitLoading"
            (show)="onUnitShow($event)"
            #unitSelect
            class="tw-w-full md:tw-w-1/3 tw-min-w-0"
          />
        </div>
      </div>

      <app-action-row>
        <app-toggle-switch
          left1
          [control]="showAdvanced"
          label="Show advanced"
          [showHelpText]="false"
        />
      </app-action-row>

      @if (showAdvanced.value) {
        @if (showDates.value) {
          <div class="tw-py-3">
            <div
              class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-0 tw-pt-4"
            >
              <app-select
                [options]="state"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.payState"
                label="Payroll/Premium state"
                class="tw-w-full md:tw-w-1/3 tw-min-w-0"
              />
              <app-select
                [options]="state"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.lossState"
                label="Loss state"
                class="tw-w-full md:tw-w-1/3 tw-min-w-0"
              />
              <app-select
                [options]="state"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.workState"
                label="Work state"
                class="tw-w-full md:tw-w-1/3 tw-min-w-0"
              />
            </div>
          </div>

          <h2
            class="tw-text-base tw-w-full tw-font-bold tw-pl-1.5 tw-pb-3 tw-text-[var(--sed-engaged-black-color)]"
          >
            Dates/Financials
          </h2>

          <div class="tw-pb-3">
            <div
              class="tw-flex tw-flex-col md:tw-flex-row tw-gap-x-0 lg:tw-gap-x-3"
            >
              <div class="tw-w-full md:tw-basis-1/3 tw-min-w-0">
                <app-range
                  label="Date of loss"
                  [control]="fg.controls.dateLossFrom"
                  [toControl]="fg.controls.dateLossTo"
                ></app-range>
                <app-range
                  label="Date reported to client"
                  [control]="fg.controls.dateReportedFrom"
                  [toControl]="fg.controls.dateReportedTo"
                ></app-range>
                <app-range
                  label="Date reported to TPA"
                  [control]="fg.controls.dateTPAFrom"
                  [toControl]="fg.controls.dateTPATo"
                ></app-range>
                <app-range
                  label="Date opened"
                  [control]="fg.controls.dateOpenedFrom"
                  [toControl]="fg.controls.dateOpenedTo"
                ></app-range>
                <app-range
                  label="Date closed"
                  [control]="fg.controls.dateClosedFrom"
                  [toControl]="fg.controls.dateClosedTo"
                ></app-range>
              </div>

              <div class="tw-w-full md:tw-basis-1/3 tw-min-w-0">
                <app-range
                  label="Last day worked"
                  [control]="fg.controls.dateLastDayFrom"
                  [toControl]="fg.controls.dateLastDayTo"
                ></app-range>
                <app-range
                  label="First day absent"
                  [control]="fg.controls.dateFirstDayFrom"
                  [toControl]="fg.controls.dateFirstDayTo"
                ></app-range>
                <app-range
                  label="Approved begin date"
                  [control]="fg.controls.dateApprovedBeginFrom"
                  [toControl]="fg.controls.dateApprovedBeginTo"
                ></app-range>
                <app-range
                  label="Approved end date"
                  [control]="fg.controls.dateApprovedEndFrom"
                  [toControl]="fg.controls.dateApprovedEndTo"
                ></app-range>
                <app-range
                  label="Return to work date"
                  [control]="fg.controls.dateRTWFrom"
                  [toControl]="fg.controls.dateRTWTo"
                ></app-range>
              </div>

              <div class="tw-w-full md:tw-basis-1/3 tw-min-w-0">
                <app-range
                  label="Incurred amount"
                  [control]="fg.controls.incurredFrom"
                  [toControl]="fg.controls.incurredTo"
                  type="Currency"
                ></app-range>
                <app-range
                  label="Reserve amount"
                  [control]="fg.controls.reserveFrom"
                  [toControl]="fg.controls.reserveTo"
                  type="Currency"
                ></app-range>
                <app-range
                  label="Paid amount"
                  [control]="fg.controls.paidAmtFrom"
                  [toControl]="fg.controls.paidAmtTo"
                  type="Currency"
                ></app-range>
              </div>
            </div>
          </div>
        }
        @if (showLocation.value) {}
        @if (showFilter.value) {
          <div class="tw-pb-3">
            <ng-container
              *ngTemplateOutlet="subHeader; context: { label: 'Filter' }"
            />
            <div
              class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-0"
            >
              <app-select
                [options]="client"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.client"
                label="Client"
                class="tw-w-full md:tw-basis-1/3 tw-min-w-0"
              />
              <app-select
                [options]="countries"
                optionLabel="name"
                optionValue="code"
                [control]="fg.controls.filter"
                label="Filter"
                class="tw-w-full md:tw-basis-1/3 tw-min-w-0"
              />
            </div>
            <div class="tw-flex">
              <div class="tw-w-full">
                <app-toast
                  >Selecting a filter excludes other search criteria</app-toast
                >
              </div>
            </div>
          </div>
        }
      }

      <div class="tw-pt-4"></div>
      <app-action-row>
        <app-button
          right1
          type="submit"
          (click)="searchClick()"
          [loading]="loading"
          label="Search"
          id="search-form-button"
          [disabled]="!fg.valid"
        />
        <app-button
          right2
          emphasis="Low"
          (click)="clearClick()"
          label="Clear"
        />
      </app-action-row>
      @if (lobNotSelected) {
        <app-toast
          center1
          type="Error"
          [dismiss]="true"
          [dismissSeconds]="10"
          (dismissed)="lobNotSelected = false"
        >
          <ng-content>Must select atleast one Line of business</ng-content>
        </app-toast>
      }
    </form>

    <ng-template #subHeader let-label="label">
      <div class="tw-py-2">
        <h2 class="tw-text-base">{{ label }}</h2>
      </div>
    </ng-template>
  `,
  styles: ``,
})
export class SearchFormComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);

  @Output()
  search = new EventEmitter<SearchInput>();

  @Output()
  clear = new EventEmitter();

  @Input()
  loading = false;

  selectedLOB: any;
  selectedClient: any;
  selectedStatus: any;
  selectedAccount: any;
  ssnType: SsnType = 'Full';
  claimNumberSearchType: SearchType = 'ClaimNumberIs';
  showSsn = true;
  lobNotSelected: boolean;
  quicksearchParam: QuickSearchOption;
  quicksearchProcessed = false;

  /** Set when quick-search full name is exactly two words; search may retry with names swapped. */
  private trySwappedNameOrderIfEmpty = false;

  @ViewChild('searchType')
  searchType: ClaimNumberComponent;

  /** Alphanumeric search fields: letters, digits, hyphen, space, apostrophe. */
  private static readonly searchAlphaNumeric = ValidatorsEx.alphaNumericWith(
    "- '",
    'Enter only alphanumeric characters, hyphens, spaces, or apostrophes',
  );

  fg = new FormGroup({
    claimNumber: new FormControl<string>(
      null,
      ValidatorsEx.alphaNumericHyphenDot(),
    ),
    firstName: new FormControl<string>(null, ValidatorsEx.alpha()),
    lastName: new FormControl<string>(null, ValidatorsEx.alpha()),
    employeeId: new FormControl<string>(
      null,
      SearchFormComponent.searchAlphaNumeric,
    ),
    ssn: new FormControl<string>(null),
    ExactXRefMatchFlag: new FormControl<boolean>(null),

    eventNumber: new FormControl<string>(
      null,
      SearchFormComponent.searchAlphaNumeric,
    ),
    driverName: new FormControl<string>(null, ValidatorsEx.alpha()),
    birthDate: new FormControl<Date>(null),
    phoneNumber: new FormControl<string>(null),
    searchOptions: new FormControl<string[]>([]),

    claimStatus: new FormControl<string[]>([]),
    claimSubstatus: new FormControl<string[]>([]),
    lob: new FormControl<string[]>([]),
    claimType: new FormControl<string[]>([]),
    caseType: new FormControl<string[]>([]),

    claimFlags: new FormControl<string[]>([]),
    policyNumber: new FormControl<string>(
      null,
      SearchFormComponent.searchAlphaNumeric,
    ),
    examinerName: new FormControl<string>(null, ValidatorsEx.alphaNumeric()),
    client: new FormControl<string>(null),
    account: new FormControl<string>(null),
    unit: new FormControl<string>(null),
    payState: new FormControl<string>(null),
    lossState: new FormControl<string>(null),
    workState: new FormControl<string>(null),
    filter: new FormControl<string>(null),

    incurredFrom: new FormControl<string>(null),
    incurredTo: new FormControl<string>(null),
    reserveFrom: new FormControl<string>(null),
    reserveTo: new FormControl<string>(null),
    paidAmtFrom: new FormControl<string>(null),
    paidAmtTo: new FormControl<string>(null),
    dateLossFrom: new FormControl<string>(null),
    dateLossTo: new FormControl<string>(null),
    dateReportedFrom: new FormControl<string>(null),
    dateReportedTo: new FormControl<string>(null),
    dateTPAFrom: new FormControl<string>(null),
    dateTPATo: new FormControl<string>(null),
    dateOpenedFrom: new FormControl<string>(null),
    dateOpenedTo: new FormControl<string>(null),
    dateClosedFrom: new FormControl<string>(null),
    dateClosedTo: new FormControl<string>(null),
    dateLastDayFrom: new FormControl<string>(null),
    dateLastDayTo: new FormControl<string>(null),
    dateFirstDayFrom: new FormControl<string>(null),
    dateFirstDayTo: new FormControl<string>(null),
    dateApprovedBeginFrom: new FormControl<string>(null),
    dateApprovedBeginTo: new FormControl<string>(null),
    dateApprovedEndFrom: new FormControl<string>(null),
    dateApprovedEndTo: new FormControl<string>(null),
    dateRTWFrom: new FormControl<string>(null),
    dateRTWTo: new FormControl<string>(null),
  });

  showDates = new FormControl<boolean>(true);
  showFinancials = new FormControl<boolean>(true);
  showFilter = new FormControl<boolean>(false);
  showLocation = new FormControl<boolean>(true);
  showAdvanced = new FormControl<boolean>(false);

  countries = [
    { name: 'Australia', code: 'AU' },
    { name: 'Brazil', code: 'BR' },
    { name: 'China', code: 'CN' },
    { name: 'Egypt', code: 'EG' },
    { name: 'France', code: 'FR' },
    { name: 'Germany', code: 'DE' },
    { name: 'India', code: 'IN' },
    { name: 'Japan', code: 'JP' },
    { name: 'Spain', code: 'ES' },
    { name: 'United States', code: 'US' },
  ];

  searchOptions = [
    { description: 'Phonetic name search', value: 'phonetic' },
    { description: 'Claimant has multiple claims', value: 'multiple-claims' },
  ];

  claimStatus: ListItem[];
  claimSubstatus: ListItem[];
  lob: ListItem[];
  claimFlag: ListItem[];
  state: ListItem[];
  claimType: ListItem[];
  caseType: ListItem[];
  client: ListItem[];
  account: ListItem[];
  unit: ListItem[];
  unitLoading = false;
  private unitLoadToken = 0;
  private unitNeedsLazyLoad = false;

  @ViewChild('unitSelect')
  unitSelect: SelectComponent;
  searchOption: ListItem[];
  tempclaimType: ListItem[];

  async ngOnInit() {
    this.inputReset();
    this.dispose.subs = this.fg.controls.lob.valueChanges.subscribe((value) => {
      if (value === null || value.length === 0) {
        this.lobClear();
      } else {
        this.lobChange(value);
      }
    });
    this.dispose.subs = this.fg.controls.client.valueChanges.subscribe(
      (value) => {
        if (value === null) {
          this.clientClear();
        } else {
          this.clientChange(value);
        }
      },
    );
    this.dispose.subs = this.fg.controls.account.valueChanges.subscribe(
      (value) => {
        if (value === null) {
          this.accountClear();
        } else {
          this.accountChange(value);
        }
      },
    );
    this.dispose.subs = this.fg.controls.claimStatus.valueChanges.subscribe(
      (value) => {
        if (value === null) {
          this.statusClear();
        } else {
          this.statusChange(value);
        }
      },
    );

    this.dispose.subs = this.route.queryParamMap.subscribe((params) => {
      this.quicksearchParam = JSON.parse(params.get('quicksearch'));
      if (this.quicksearchParam) {
        if (this.searchType) {
          this.searchType.searchType = this.claimNumberSearchType;
          this.quickSearch(this.quicksearchParam);
          this.searchClick();
          this.cdr.detectChanges();
          this.quicksearchProcessed = true;
          this.router.navigate([], {
            queryParams: { tab: 'search', quicksearch: null },
            queryParamsHandling: 'merge',
          });
        } else {
          this.quicksearchProcessed = false;
        }
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    await this.api.list.get().then((listResponse) => {
      this.claimStatus = listResponse.claimStatus;
      this.claimSubstatus = listResponse.claimSubstatus;
      this.lob = listResponse.linesOfBusiness;
      this.claimFlag = listResponse.claimFlags;
      this.state = listResponse.states;
      this.claimType = listResponse.claimTypes;
      this.tempclaimType = listResponse.claimTypes;
      this.client = listResponse.clients;
    });
    if (this.client.length === 1) {
      this.fg.controls.client.setValue(this.client[0].value);
      this.fg.controls.client.disable();
    }

    this.enableAllControls();
    await this.loadAllLobOptions();

    if (this.quicksearchParam && !this.quicksearchProcessed) {
      this.searchType.searchType = this.claimNumberSearchType;
      this.quickSearch(this.quicksearchParam);
      this.searchClick();
      this.cdr.detectChanges();
      this.quicksearchProcessed = true;
      this.router.navigate([], {
        queryParams: { tab: 'search', quicksearch: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  inputReset() {
    this.fg.controls.account.disable();
    this.fg.controls.unit.disable();
  }

  async ddlReset() {
    this.enableAllControls();
    await this.loadAllLobOptions();
  }

  protected searchClick() {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      this.loading = false;
      return;
    }

    const value = { ...this.fg.value };
    value.lob = this.getListItems(value.lob);
    value.claimStatus = this.getListItems(value.claimStatus);
    value.claimSubstatus = this.getListItems(value.claimSubstatus);
    value.claimType = this.getListItems(value.claimType);
    value.caseType = this.getListItems(value.caseType);
    value.searchOptions = this.getListItems(value.searchOptions);
    value.client =
      this.client?.length === 1 ? this.client[0].value : value.client;
    value.account =
      this.account?.length === 1 ? this.account[0].value : value.account;
    value.unit = this.unit?.length === 1 ? this.unit[0].value : value.unit;
    value.ExactXRefMatchFlag =
      this.claimNumberSearchType === this.searchType?.searchType ? true : false;
    // Allow '-', ' ', "'" in the UI for name/ID fields; strip before API.
    // Claim number keeps its own allowed set and is sent as entered.
    value.firstName = this.stripSearchSpecialChars(value.firstName);
    value.lastName = this.stripSearchSpecialChars(value.lastName);
    value.employeeId = this.stripSearchSpecialChars(value.employeeId);
    value.eventNumber = this.stripSearchSpecialChars(value.eventNumber);
    value.policyNumber = this.stripSearchSpecialChars(value.policyNumber);
    value.examinerName = this.stripSearchSpecialChars(value.examinerName);
    value.driverName = this.stripSearchSpecialChars(value.driverName);
    const trySwap = this.trySwappedNameOrderIfEmpty;
    if (trySwap) {
      this.trySwappedNameOrderIfEmpty = false;
    }
    this.search.emit({
      ...value,
      ...(trySwap ? { trySwappedNameOrderIfEmpty: true } : {}),
    } as SearchInput);
  }

  /** Remove hyphens, spaces, and apostrophes from search text before API. */
  private stripSearchSpecialChars(
    value: string | null | undefined,
  ): string | null {
    if (value == null) {
      return null;
    }
    const stripped = String(value).replace(/[- ']/g, '');
    return stripped.length > 0 ? stripped : null;
  }

  getListItems(items: string[]): string[] {
    if (!items || items.length === 0) {
      return [];
    } else {
      return items;
    }
  }

  async clearClick() {
    this.fg.reset();
    // Use setTimeout to ensure clearing happens after form reset
    this.dispose.timeouts = setTimeout(() => {
      this.clientClear();
      this.ddlReset();
      if (this.client.length === 1) {
        this.fg.controls.client.setValue(this.client[0].value);
        this.fg.controls.client.disable();
      }
      if (this.account.length === 1) {
        this.fg.controls.account.setValue(this.account[0].value);
        this.fg.controls.account.disable();
      }
      if (this.unit.length === 1) {
        this.fg.controls.unit.setValue(this.unit[0].value);
        this.fg.controls.unit.disable();
      }
    }, 0);
    this.clear.emit();
  }

  formSubmit() {
    focusFormError();
  }

  async lobClear() {
    this.ddlReset();
  }

  enableAllControls() {
    this.fg.controls.driverName.enable();
    this.fg.controls.policyNumber.enable();
    this.fg.controls.incurredFrom.enable();
    this.fg.controls.incurredTo.enable();
    this.fg.controls.reserveFrom.enable();
    this.fg.controls.reserveTo.enable();
    this.fg.controls.paidAmtFrom.enable();
    this.fg.controls.paidAmtTo.enable();
    this.fg.controls.dateLossFrom.enable();
    this.fg.controls.dateLossTo.enable();
    this.fg.controls.dateOpenedFrom.enable();
    this.fg.controls.dateOpenedTo.enable();
    this.fg.controls.dateLastDayFrom.enable();
    this.fg.controls.dateLastDayTo.enable();
    this.fg.controls.dateFirstDayFrom.enable();
    this.fg.controls.dateFirstDayTo.enable();
    this.fg.controls.dateApprovedBeginFrom.enable();
    this.fg.controls.dateApprovedBeginTo.enable();
    this.fg.controls.dateApprovedEndFrom.enable();
    this.fg.controls.dateApprovedEndTo.enable();
    this.fg.controls.dateRTWFrom.enable();
    this.fg.controls.dateRTWTo.enable();
    this.fg.controls.payState.enable();
    this.fg.controls.lossState.enable();
    this.fg.controls.workState.enable();
    this.fg.controls.claimType.enable();
    this.fg.controls.caseType.enable();
  }

  async loadAllLobOptions() {
    // Load all LOB-specific options when all LOBs are selected
    const listResponse = await this.api.list.get();
    this.claimStatus = listResponse.claimStatus;
    this.claimType = listResponse.claimTypes;
    this.searchOptions = this.searchOptions.filter(
      (item) => item.value !== 'multiple-claims',
    );
    const searchOptions = (this.fg.controls.searchOptions.value || []).filter(
      (item) => item !== 'multiple-claims',
    );
    this.fg.controls.searchOptions.setValue(searchOptions);
    if (!this.caseType || this.caseType.length === 0) {
      this.caseType = listResponse.caseTypes;
    }
  }

  enableByLob(control: FormControl, lobs: string[]) {
    const match = lobs.filter((item) =>
      this.selectedLOB.includes(this.leaveLOB(item)),
    );
    if (match.length > 0) {
      control.enable();
    } else {
      control.disable();
    }
  }

  enableByLobDDLProp(
    list: any[],
    incidentLobs: string[],
    rescindedLobs: string[],
    searchOptionLobs: string[],
    searchList: any[],
  ) {
    const incidentmatch = incidentLobs.filter((item) =>
      this.selectedLOB.includes(this.leaveLOB(item)),
    );
    const rescindedmatch = rescindedLobs.filter((item) =>
      this.selectedLOB.includes(this.leaveLOB(item)),
    );
    const searchOptionmatch = searchOptionLobs.filter((item) =>
      this.selectedLOB.includes(this.leaveLOB(item)),
    );
    if (
      incidentmatch.length > 0 &&
      this.claimStatus.findIndex((item) => item.value === 'I') == -1
    ) {
      // Add value if not present
      const exists = list.some((item) => item.value === 'I');
      if (!exists) {
        const commentData = {} as ListItem;
        commentData.value = 'I';
        commentData.description = 'Incident';
        list.push(commentData);
      }
    }
    if (
      rescindedmatch.length > 0 &&
      this.claimStatus.findIndex((item) => item.value === 'R') == -1
    ) {
      // Add value if not present
      const exists = list.some((item) => item.value === 'R');
      if (!exists) {
        const commentData = {} as ListItem;
        commentData.value = 'R';
        commentData.description = 'Rescinded';
        list.push(commentData);
      }
    }
    if (this.getIntersectionLength(this.selectedLOB, incidentLobs) === 0) {
      // Remove specific value from list
      list = list.filter((item) => item.value !== 'I');
      const claimStatus = (this.fg.controls.claimStatus.value || []).filter(
        (item) => item !== 'I',
      );
      this.fg.controls.claimStatus.setValue(claimStatus);
    }
    if (this.getIntersectionLength(this.selectedLOB, rescindedLobs) === 0) {
      // Remove specific value from list
      list = list.filter((item) => item.value !== 'R');
      const claimStatus = (this.fg.controls.claimStatus.value || []).filter(
        (item) => item !== 'R',
      );
      this.fg.controls.claimStatus.setValue(claimStatus);
    }
    // Update reference for Angular change detection
    this.claimStatus = [...list];

    if (
      searchOptionmatch.length > 0 &&
      this.searchOptions.findIndex(
        (item) => item.value === 'multiple-claims',
      ) == -1
    ) {
      // Add value if not present
      const exists = searchList.some(
        (item) => item.value === 'multiple-claims',
      );
      if (!exists) {
        const commentData = {} as ListItem;
        commentData.value = 'multiple-claims';
        commentData.description = 'Claimant has multiple claims';
        searchList.push(commentData);
      }
    } else if (
      this.getIntersectionLength(this.selectedLOB, searchOptionLobs) === 0
    ) {
      // Remove specific value from searchList
      searchList = searchList.filter(
        (item) => item.value !== 'multiple-claims',
      );
    }
    this.searchOptions = [...searchList];
    this.cdr.detectChanges();
  }

  async lobChange(event: any) {
    this.selectedLOB = event;
    // If no LOB is selected or all LOBs are selected, enable all controls and load all options
    if (
      this.selectedLOB.length === 0 ||
      this.selectedLOB.length === this.lob.length
    ) {
      this.enableAllControls();
      await this.loadAllLobOptions();
    } else if (this.selectedLOB.length > 0) {
      const lobs = {
        driverLOB: ['AU', 'GL', 'PR'],
        policyLOB: ['WC', 'AU', 'GL', 'PR'],
        incurredLOB: ['WC', 'AU', 'GL', 'PR'],
        reserveLOB: ['WC', 'AU', 'GL', 'PR'],
        paidLOB: ['WC', 'AU', 'GL', 'PR'],
        dateOfLossLOB: ['WC', 'DS', 'AU', 'GL', 'PR'],
        dateOpenedLOB: ['WC', 'DS', 'AU', 'GL', 'PR'],
        lastDayWorkedLOB: ['DS', 'LV'],
        firstDayAbsentLOB: ['WC', 'DS', 'LV', 'AD'],
        approvedBeginDateLOB: ['LV', 'AD'],
        approvedEndDateLOB: ['LV', 'AD'],
        rtwDateLOB: ['LV', 'AD'],
        statusIncidentLOB: ['WC', 'DS', 'AU', 'GL', 'PR'],
        statusRescindedLOB: ['AD'],
        claimTypeLOB: ['WC', 'DS', 'LV'],
        caseTypeLOB: ['LV'],
        searchOptionLOB: ['WC', 'DS', 'LV', 'AU', 'GL', 'PR'],
        payrollPremiumLOB: ['WC', 'AU', 'GL', 'PR'],
        lossStateLOB: ['WC', 'AU', 'GL', 'PR'],
        workStateLOB: ['DS', 'LV'],
      };

      // TODO: implement these for each if condition in loop below
      this.enableByLob(this.fg.controls.driverName, lobs.driverLOB);
      this.enableByLob(this.fg.controls.policyNumber, lobs.policyLOB);
      this.enableByLob(this.fg.controls.incurredFrom, lobs.incurredLOB);
      this.enableByLob(this.fg.controls.incurredTo, lobs.incurredLOB);
      this.enableByLob(this.fg.controls.reserveFrom, lobs.reserveLOB);
      this.enableByLob(this.fg.controls.reserveTo, lobs.reserveLOB);
      this.enableByLob(this.fg.controls.paidAmtFrom, lobs.paidLOB);
      this.enableByLob(this.fg.controls.paidAmtTo, lobs.paidLOB);
      this.enableByLob(this.fg.controls.dateLossFrom, lobs.dateOfLossLOB);
      this.enableByLob(this.fg.controls.dateLossTo, lobs.dateOfLossLOB);
      this.enableByLob(this.fg.controls.dateOpenedFrom, lobs.dateOpenedLOB);
      this.enableByLob(this.fg.controls.dateOpenedTo, lobs.dateOpenedLOB);
      this.enableByLob(this.fg.controls.dateLastDayFrom, lobs.lastDayWorkedLOB);
      this.enableByLob(this.fg.controls.dateLastDayTo, lobs.lastDayWorkedLOB);
      this.enableByLob(
        this.fg.controls.dateFirstDayFrom,
        lobs.firstDayAbsentLOB,
      );
      this.enableByLob(this.fg.controls.dateFirstDayTo, lobs.firstDayAbsentLOB);
      this.enableByLob(
        this.fg.controls.dateApprovedBeginFrom,
        lobs.approvedBeginDateLOB,
      );
      this.enableByLob(
        this.fg.controls.dateApprovedBeginTo,
        lobs.approvedBeginDateLOB,
      );
      this.enableByLob(
        this.fg.controls.dateApprovedEndFrom,
        lobs.approvedEndDateLOB,
      );
      this.enableByLob(
        this.fg.controls.dateApprovedEndTo,
        lobs.approvedEndDateLOB,
      );
      this.enableByLob(this.fg.controls.dateRTWFrom, lobs.rtwDateLOB);
      this.enableByLob(this.fg.controls.dateRTWTo, lobs.rtwDateLOB);
      this.enableByLob(this.fg.controls.payState, lobs.payrollPremiumLOB);
      this.enableByLob(this.fg.controls.lossState, lobs.lossStateLOB);
      this.enableByLob(this.fg.controls.workState, lobs.workStateLOB);
      this.enableByLob(this.fg.controls.caseType, lobs.caseTypeLOB);
      this.enableByLob(this.fg.controls.claimType, lobs.claimTypeLOB);

      this.enableByLobDDLProp(
        this.claimStatus,
        lobs.statusIncidentLOB,
        lobs.statusRescindedLOB,
        lobs.searchOptionLOB,
        this.searchOptions,
      );

      for (const key in lobs) {
        if (key == 'claimTypeLOB') {
          const claimTypeGroupMatch = lobs[key].filter((item) =>
            this.selectedLOB.includes(this.leaveLOB(item)),
          );
          this.claimType = [];
          const updatedClaimTypes = [];
          for (const match of claimTypeGroupMatch) {
            if (match == 'WC') {
              updatedClaimTypes.push(
                ...this.tempclaimType.filter((item) => item.value == 'WC'),
              );
            }
            if (match == 'DS') {
              updatedClaimTypes.push(
                ...this.tempclaimType.filter((item) => item.value == 'DS'),
              );
            }
            if (this.leaveLOB(match) == this.leaveLOB('LA')) {
              updatedClaimTypes.push(
                ...this.tempclaimType.filter(
                  (item) => item.value == this.leaveLOB('LA'),
                ),
              );
            }
            this.claimType = updatedClaimTypes;
          }
          this.filterTypes();
        } else if (key == 'caseTypeLOB') {
          const caseTypeLOBMatch = lobs[key].filter((item) =>
            this.selectedLOB.includes(this.leaveLOB(item)),
          );
          if (caseTypeLOBMatch.length > 0) {
            this.api.list.get().then((listResponse) => {
              this.caseType = listResponse.caseTypes;
            });
          } else {
            this.caseType = [];
            this.fg.controls.caseType.setValue(null);
          }
        }
      }
    }
  }

  statusChange(event: any) {
    const selectedStatus = event;

    const updatedClaimType = [];

    for (const item of this.claimType) {
      const newItem = { ...item };
      newItem.items = item.items ? [...item.items] : [];
      updatedClaimType.push(newItem);
    }

    for (const item of updatedClaimType) {
      if (item.value == 'WC') {
        const innerItem = item.items;
        const index = innerItem.findIndex((item) => item.value === 'IO');
        if (selectedStatus.length === 0 && index === -1) {
          const commentData = {} as ListItem;
          commentData.value = 'IO';
          commentData.description = 'Incident Only';
          innerItem.push(commentData);
        }
        for (const data of selectedStatus) {
          if (data === 'I' && index === -1) {
            const commentData = {} as ListItem;
            commentData.value = 'IO';
            commentData.description = 'Incident Only';
            innerItem.push(commentData);
          }
          if (
            !selectedStatus.includes('I') &&
            index != -1 &&
            (data == 'C' || data == 'O' || data == 'R')
          ) {
            item.items.splice(index, 1);
          }
        }
      }
      this.claimType = updatedClaimType;
    }
    this.filterTypes();
  }

  statusClear() {
    const updatedClaimType = [];

    for (const item of this.claimType) {
      const newItem = { ...item };
      newItem.items = item.items ? [...item.items] : [];
      updatedClaimType.push(newItem);
    }

    for (const item of updatedClaimType) {
      if (item.value == 'WC') {
        if (item.items.findIndex((item) => item.value === 'IO') === -1) {
          const innerItem = item.items;
          const commentData = {} as ListItem;
          commentData.value = 'IO';
          commentData.description = 'Incident Only';
          innerItem.push(commentData);
        }
      }
      this.claimType = updatedClaimType;
    }
    this.filterTypes();
  }

  async clientChange(event: any) {
    this.selectedClient = event;
    this.resetUnitLoad();

    // Clear account DDL and its form control
    this.selectedAccount = null;
    this.account = [];
    this.fg.controls.account.setValue(null);
    this.fg.controls.account.disable();

    // Also clear unit DDL if it's dependent on account
    this.unit = [];
    this.fg.controls.unit.setValue(null);
    this.fg.controls.unit.disable();

    if (this.selectedClient != null) {
      this.account = await this.api.list.getAccount(this.selectedClient);
      if (this.account.length === 1) {
        this.fg.controls.account.enable();
        this.fg.controls.account.setValue(this.account[0].value);
        this.fg.controls.account.disable();
      } else {
        if (this.account.length > 0) {
          this.fg.controls.account.enable();
        }
        this.fg.controls.unit.enable();
        this.unitNeedsLazyLoad = true;
      }
    }
  }

  clientClear() {
    this.selectedClient = null;
    this.selectedAccount = null;
    this.resetUnitLoad();
    this.account = [];
    this.unit = [];
    this.fg.controls.account.setValue(null);
    this.fg.controls.unit.setValue(null);
    this.fg.controls.account.disable();
    this.fg.controls.unit.disable();
  }

  async accountChange(event: any) {
    this.selectedAccount = event;
    const token = ++this.unitLoadToken;
    const previousUnitValue = this.fg.controls.unit.value;

    // Clear unit DDL and its form control
    this.unit = [];
    this.fg.controls.unit.setValue(null);

    if (this.selectedAccount != null) {
      this.unitNeedsLazyLoad = false;
      this.unitLoading = true;
      this.fg.controls.unit.disable();
      try {
        const units = await this.api.list.getUnit(
          this.selectedClient,
          this.selectedAccount,
        );
        if (token !== this.unitLoadToken) return;
        this.unit = units;
        if (this.unit.length > 0) {
          this.fg.controls.unit.enable();
          if (this.unit.length === 1) {
            this.fg.controls.unit.setValue(this.unit[0].value);
            this.fg.controls.unit.disable();
          } else if (
            previousUnitValue &&
            this.unit.some((u) => u.value === previousUnitValue)
          ) {
            this.fg.controls.unit.setValue(previousUnitValue);
          }
        }
      } finally {
        if (token === this.unitLoadToken) this.unitLoading = false;
      }
    } else {
      this.fg.controls.unit.enable();
      this.unitNeedsLazyLoad = true;
    }
  }

  accountClear() {
    this.selectedAccount = null;
    this.resetUnitLoad();
    this.unit = [];
    this.fg.controls.unit.setValue(null);
    if (this.selectedClient != null) {
      this.fg.controls.unit.enable();
      this.unitNeedsLazyLoad = true;
    } else {
      this.fg.controls.unit.disable();
    }
  }

  async onUnitShow(select: SelectComponent) {
    if (!this.unitNeedsLazyLoad || this.unitLoading) {
      if (this.unitLoading) select.close();
      return;
    }

    select.close();
    this.unitNeedsLazyLoad = false;
    const token = ++this.unitLoadToken;

    const unitsPromise = this.api.list.getUnit(
      this.selectedClient,
      this.selectedAccount ?? 0,
    );
    // Skip the loading label when the cached list returns immediately.
    const loadingTimer = setTimeout(() => {
      if (token !== this.unitLoadToken) return;
      this.unitLoading = true;
      this.cdr.detectChanges();
    }, 200);
    this.dispose.timeouts = loadingTimer;
    // Let the panel finish closing before a cached response reopens it.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    try {
      const units = await unitsPromise;
      if (token !== this.unitLoadToken) return;
      this.unit = units;
      if (this.unit.length === 1) {
        this.fg.controls.unit.setValue(this.unit[0].value);
        this.fg.controls.unit.disable();
      }
    } finally {
      clearTimeout(loadingTimer);
      if (token === this.unitLoadToken) {
        this.unitLoading = false;
        if (this.unit.length > 1) {
          this.cdr.detectChanges();
          this.dispose.timeouts = setTimeout(() => this.unitSelect?.open(), 0);
        }
      }
    }
  }

  private resetUnitLoad() {
    this.unitLoadToken++;
    this.unitLoading = false;
    this.unitNeedsLazyLoad = false;
  }

  leaveLOB(lob: string): string {
    if (lob == 'LV' || lob == 'LA' || lob == 'FM') {
      return 'LA';
    } else {
      return lob;
    }
  }

  getIntersectionLength<T>(arr1: T[], arr2: T[]): number {
    return arr1.filter((value) => arr2.includes(value)).length;
  }

  quickSearch(option: QuickSearchOption): void {
    this.fg.reset();
    switch (option.type) {
      case 'claim-number':
        this.searchType.searchType = 'ClaimNumberIs';
        this.searchType.setLabel();
        this.fg.controls.claimNumber.setValue(option.value);
        break;
      case 'claim-event-number':
        this.searchType.searchType = 'ClaimNumberBeginsWith';
        this.searchType.setLabel();
        this.fg.controls.claimNumber.setValue(option.value);
        break;
      case 'employee-id':
        this.fg.controls.employeeId.setValue(option.value);
        break;
      case 'ssn':
      case 'ssn-last-4': {
        const ssnValue = option.value;
        this.ssnType = ssnValue.length === 4 ? 'LastFour' : 'Full';
        this.fg.controls.ssn.setValue(ssnValue);
        break;
      }
      case 'last-name':
        this.fg.controls.lastName.setValue(option.value);
        break;
      case 'policy-number':
        this.fg.controls.policyNumber.setValue(option.value);
        break;
      case 'full-name': {
        const parts = option.value.split(/\s+/).filter(Boolean);
        this.fg.controls.firstName.setValue(parts[0] ?? null);
        this.fg.controls.lastName.setValue(
          parts.length > 1 ? parts.slice(1).join(' ') : null,
        );
        this.trySwappedNameOrderIfEmpty = parts.length === 2;
        break;
      }
      default:
        break;
    }
  }

  filterTypes() {
    const currentClaimType = this.fg.controls.claimType.value;
    const newClaimTypes = [] as ListItem[];
    for (const item of this.claimType) {
      newClaimTypes.push(...item.items);
    }
    const filteredClaimType = currentClaimType?.filter((value) =>
      newClaimTypes.some((item) => item.value === value),
    );
    this.fg.controls.claimType.setValue(filteredClaimType);
  }
}
